import apiClient from "@/shared/services/apiClient";
import type {
  Task,
  Subtask,
  CreateTaskPayload,
  CreateSubtaskPayload,
  MoveTaskPayload,
  TaskUserSearchResult,
  TaskUserSearchFilters,
} from "@/features/tasks/types/task";

export interface GetTasksFilters {
  column_id?: string;
  team_id?: string;
  category?: string;
  assignee_id?: string;
  /** Pass 'personal' to get only tasks with no team (My Board). */
  board?: "personal";
}

export async function getTasks(filters?: GetTasksFilters): Promise<Task[]> {
  const { data } = await apiClient.get("/tasks", { params: filters });
  return data;
}

export async function getTask(id: string): Promise<Task> {
  const { data } = await apiClient.get(`/tasks/${id}`);
  return data;
}

export async function createTask(payload: CreateTaskPayload): Promise<Task> {
  const { data } = await apiClient.post("/tasks", payload);
  return data;
}

export async function updateTask(
  id: string,
  payload: Partial<CreateTaskPayload>
): Promise<Task> {
  const { data } = await apiClient.patch(`/tasks/${id}`, payload);
  return data;
}

export async function moveTask(
  id: string,
  payload: MoveTaskPayload
): Promise<Task> {
  const { data } = await apiClient.patch(`/tasks/${id}/move`, payload);
  return data;
}

export async function deleteTask(id: string): Promise<void> {
  await apiClient.delete(`/tasks/${id}`);
}

// ─── Sub-tasks ────────────────────────────────────────────────────────────────

export async function createSubtask(
  parentId: string,
  payload: CreateSubtaskPayload
): Promise<Subtask> {
  const { data } = await apiClient.post(`/tasks/${parentId}/subtasks`, payload);
  return data;
}

export async function listSubtasks(parentId: string): Promise<Subtask[]> {
  const { data } = await apiClient.get(`/tasks/${parentId}/subtasks`);
  return data;
}

export async function completeSubtask(id: string): Promise<Task> {
  const { data } = await apiClient.patch(`/tasks/${id}/complete`);
  return data;
}

export async function resetSubtaskCompletion(
  id: string,
  userId: string,
): Promise<Subtask> {
  const { data } = await apiClient.patch(`/tasks/${id}/reset-completion`, {
    user_id: userId,
  });
  return data;
}

export async function completeTask(id: string): Promise<Task> {
  const { data } = await apiClient.patch(`/tasks/${id}/complete`);
  return data;
}

// ─── Subtask responses (comments + attachments from mobile workers) ───────────

export interface SubtaskComment {
  id: string;
  text: string;
  user_id: string;
  user_name: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  profile_pic_id?: string;
  created_at: string;
  parent_comment_id?: string | null;
}

export interface SubtaskAttachment {
  id: string;
  display_name: string;
  file_type: string;
  file_size: number;
  created_at: string;
  uploader_user_id: string;
  uploader_first_name: string;
  uploader_last_name: string;
  uploader_profile_pic_id?: string;
}

export async function getSubtaskComments(subtaskRowId: number): Promise<SubtaskComment[]> {
  const { data } = await apiClient.get(`/comments/subtask/${subtaskRowId}`);
  const raw = Array.isArray(data) ? data : (data.comments ?? []);
  return raw.map((r: any): SubtaskComment => ({
    id:              String(r.comment_id),
    text:            r.content_text,
    user_id:         String(r.user_id),
    user_name:       r.user_name ?? `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim(),
    first_name:      r.first_name ?? "",
    middle_name:     r.middle_name,
    last_name:       r.last_name ?? "",
    profile_pic_id:  r.author_profile_pic_id ?? undefined,
    created_at:      r.created_at,
    parent_comment_id: r.parent_comment_id != null ? String(r.parent_comment_id) : null,
  }));
}

export async function addSubtaskComment(
  subtaskRowId: number,
  text: string,
  parentCommentId?: string | null,
): Promise<void> {
  await apiClient.post("/comments", {
    relation_type: "subtask",
    relation_id: subtaskRowId,
    content_text: text,
    parent_comment_id: parentCommentId ?? null,
  });
}

export async function getSubtaskAttachments(subtaskId: string): Promise<SubtaskAttachment[]> {
  const { data } = await apiClient.get(`/attachments/subtask/${subtaskId}`);
  const raw = Array.isArray(data) ? data : (data.attachments ?? []);
  return raw.map((r: any): SubtaskAttachment => ({
    id:                      String(r.attachment_id),
    display_name:            r.display_name,
    file_type:               r.file_type,
    file_size:               r.file_size ?? 0,
    created_at:              r.created_at,
    uploader_user_id:        String(r.created_by ?? ""),
    uploader_first_name:     r.first_name ?? r.uploader_first_name ?? "",
    uploader_last_name:      r.last_name  ?? r.uploader_last_name  ?? "",
    uploader_profile_pic_id: r.profile_pic_id ?? r.uploader_profile_pic_id,
  }));
}

// ─── User search ──────────────────────────────────────────────────────────────

export async function searchTaskUsers(
  filters: TaskUserSearchFilters
): Promise<TaskUserSearchResult[]> {
  const { data } = await apiClient.get("/tasks/users/search", { params: filters });
  return data;
}
